import React, { useState, useContext, useRef, useEffect, useMemo } from "react";
import { IServicesContext, ServicesContext } from "../../context/servicesContext";
import { ActionSidebarsContext } from "../../context/actionSidebarsContext";
import { IActionSidebarsContext } from "../../types/actionSidebars";
import Button from "../../components/ui/button/Button";
import { useTranslation } from "react-i18next";
import Tooltip from "../../components/ui/tooltip/Tooltip";
import { getAdditionalServicesStatusIcon, getMainServicesStatusIcon } from "../../utils/helpers/statusIcons";
import { CircularProgress } from "@mui/material";

// Grid
import { AgGridReact } from "ag-grid-react";
import { groupServices } from "../../utils/helpers/groupServices";

const Services: React.FC = () => {
    const { t } = useTranslation();

    const gridRef = useRef<any>(null);

    const { setSidebarAddServiceOpen } = useContext(ActionSidebarsContext) as IActionSidebarsContext;
    const { services } = useContext(ServicesContext) as IServicesContext;

    const [showedTab, setShowedTab] = useState<"mainServices" | "additionalServices">('mainServices');
    const [visualizationType, setVisualizationType] = useState<"grid" | "table">('grid');

    useEffect(() => {
        setSidebarAddServiceOpen(false);
        setCurrentFilter(null);
    }, [showedTab]);

    const onGridReady = () => {
        if (gridRef.current) {
            gridRef.current.api.setDomLayout('autoHeight');
        }
    }

    const openSidebarServices = () => {
        setSidebarAddServiceOpen(true);

    }

    const filterDropdownRef = useRef<null | HTMLDivElement>(null);
    const [isOpenFilters, setIsOpenFilters] = useState<boolean>(false);
    const [currentFilter, setCurrentFilter] = useState<null | string | number>(null);
    const openFilters = () => {
        if (!isOpenFilters) {
            setIsOpenFilters(true);
        }
    }

    useEffect(() => {
        const listener = (e: any) => {
            if (!!filterDropdownRef.current && !filterDropdownRef.current.contains(e.target) && !isOpenFilters) {
                setIsOpenFilters(false);
            }
        }
        document.addEventListener('click', listener);
        return () => {
            document.removeEventListener('click', listener);
        }
    }, []);

    const groupServiceCategories = useMemo(() => {
        if (services !== null) {
            if (showedTab === "mainServices") {
                return groupServices([...services.mainServices], "status");
            } else if (showedTab === "additionalServices") {
                return groupServices([...services.additionalServices], "status");
            }
        }
        return [];
    }, [services, showedTab]);

    const optionNames: Record<number, string> = {
        605530000: "GPS Trattore",
        605530001: "GPS Semirimorchio"
    };

    const filterServices = useMemo(() => {
        if (!services) return [];

        if (showedTab === "mainServices") {
            return [...services.mainServices].filter(s => currentFilter !== null ? s.status === currentFilter : true);
        }

        if (showedTab === "additionalServices") {
            return services.additionalServices.flatMap(s => {

                if (s.options && s.options.length > 0) {

                    // Se il servizio ha options, creiamo una card per ogni opzione selezionata
                    return s.options.map((option: any) => ({
                        ...s,
                        name: `${s.name} - ${optionNames[option]}`, // Aggiungiamo il valore dell'opzione accanto al nome
                        optionKey: option.key, // Chiave univoca per l'opzione
                    }));
                }
                return s; // Se non ha options, viene aggiunto normalmente
            }).filter(s => currentFilter !== null ? s.status === currentFilter : true);
        }

        return [];
    }, [services, showedTab, currentFilter]);


    const columnDefs = useMemo(() => [
        { field: 'type', headerName: t('screens.services.tableHeaders.category') },
        { field: 'name', headerName: t('screens.services.tableHeaders.serviceType') },
        { field: 'description', headerName: t('screens.services.tableHeaders.info') },
        {
            field: 'statusCode', headerName: t('screens.services.tableHeaders.status'), cellRenderer: (props: any) => {
                return (
                    <div className="flex items-center h-full">
                        {
                            showedTab === "mainServices" &&
                            <div>
                                {
                                    getMainServicesStatusIcon(props.data.statusCode)
                                }
                            </div>
                        }

                        {
                            showedTab === "additionalServices" &&
                            <div>
                                {
                                    getAdditionalServicesStatusIcon(props.data.statusCode)
                                }
                            </div>
                        }

                        <div className="ml-1">{props.data.status}</div>
                    </div>
                )
            }
        }
    ], [filterServices, showedTab, t]);

    const changeFilter = (value: null | string | number) => () => {
        setCurrentFilter(value);
        setIsOpenFilters(false);
    }

    return (
        <div className="p-6 md:p-8">
            <div className="md:flex items-start justify-between mb-6 md:mb-[14px]">
                <h1 className="mb-4 md:mb-0 text-[32px] font-bold leading-[37.5px]">
                    {t('screens.services.heading')}
                </h1>
                <Button
                    callback={openSidebarServices}
                >
                    <div className="flex items-center">
                        <span className="mr-[12px]">{t('common.buttonActions.services.addServices')}</span>
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-[18px] h-[18px]">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m6-6H6" />
                        </svg>
                    </div>
                </Button>
            </div>

            {/* Mostra CircularProgress quando services è null */}
            {!services ? (
                <div className="flex justify-center items-center h-64">
                    <CircularProgress size={60} sx={{ color: '#CF0A2C' }} />
                </div>
            ) : (
                <>
                    {/* TABS */}
                    <div className="flex items-center mb-[30px]">
                        <div
                            onClick={() => setShowedTab("mainServices")}
                            className={"mr-4 rounded-[30px] py-1 px-4 uppercase font-semibold text-[12px] md:text-sm leading-[17px] cursor-pointer" + (showedTab === 'mainServices' ? ' bg-red text-white' : ' bg-grey')}
                        >
                            {t('screens.services.tabMainServices')}
                        </div>
                        <div
                            onClick={() => setShowedTab("additionalServices")}
                            className={"rounded-[30px] py-1 px-4 uppercase font-semibold text-[12px] md:text-sm leading-[17px] cursor-pointer" + (showedTab === 'additionalServices' ? ' bg-red text-white' : ' bg-grey')}
                        >
                            {t('screens.services.tabAdditionalServices')}
                        </div>
                    </div>

                    <div className="flex items-center justify-between mb-[26px]">
                        {/* Grid or Table buttons */}
                        <div className="hidden md:flex items-center">
                            <div
                                className={"mr-[10px] cursor-pointer h-6 w-6 rounded-[4px] flex items-center justify-center" + (visualizationType === 'grid' ? ' bg-red' : '')}
                                onClick={() => setVisualizationType('grid')}
                            >
                                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M0 0H6V6H0V0ZM8 0H14V6H8V0ZM0 8H6V14H0V8Z" fill={visualizationType === 'grid' ? "#FFFFFF" : "#6F6F6F"} />
                                    <path d="M14 8H8V14H14V8Z" fill={visualizationType === 'grid' ? "#FFFFFF" : "#6F6F6F"} />
                                </svg>
                            </div>

                            <div
                                className={"cursor-pointer h-6 w-6 rounded-[4px] flex items-center justify-center" + (visualizationType === 'table' ? ' bg-red' : '')}
                                onClick={() => setVisualizationType('table')}
                            >
                                <svg width="16" height="10" viewBox="0 0 16 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M0 0H16V2H0V0ZM0 4H16V6H0V4ZM0 8H16V10H0V8Z" fill={visualizationType === 'table' ? "#FFFFFF" : "#6F6F6F"} />
                                </svg>
                            </div>
                        </div>

                        {/* FILTERS */}
                        {
                            ((showedTab === "mainServices" && services.mainServices.length > 0) || (showedTab === "additionalServices" && services.additionalServices.length > 0)) &&
                            <div ref={filterDropdownRef} onClick={openFilters} className="relative flex items-center cursor-pointer select-none">
                                <div className="mr-[10px]">
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M20 3H4C3.73478 3 3.48043 3.10536 3.29289 3.29289C3.10536 3.48043 3 3.73478 3 4V6.59C3 7.113 3.213 7.627 3.583 7.997L9 13.414V21C9.0002 21.1704 9.04387 21.3379 9.1269 21.4867C9.20992 21.6354 9.32955 21.7605 9.47444 21.8502C9.61934 21.9398 9.78471 21.9909 9.9549 21.9988C10.1251 22.0066 10.2945 21.9709 10.447 21.895L14.447 19.895C14.786 19.725 15 19.379 15 19V13.414L20.417 7.997C20.787 7.627 21 7.113 21 6.59V4C21 3.73478 20.8946 3.48043 20.7071 3.29289C20.5196 3.10536 20.2652 3 20 3Z" fill="#CF0A2C" />
                                    </svg>
                                </div>
                                <div className="uppercase font-semibold text-sm leading-[17px]">
                                    {
                                        currentFilter === null &&
                                        <span>
                                            {t('screens.services.filter')}
                                        </span>
                                    }
                                    {
                                        currentFilter !== null &&
                                        <span className="uppercase font-semibold text-sm leading-[17px]">
                                            <span className="text-red">{t('screens.services.filter')}:</span>
                                            <span className="ml-1">{currentFilter}</span>
                                        </span>
                                    }
                                </div>

                                {
                                    isOpenFilters &&
                                    <div className="absolute z-10 w-max right-[100%] translate-x-[100%] md:right-0 md:translate-x-0 top-[calc(100%+10px)] bg-white border border-red rounded-[8px] p-4">
                                        <div onClick={changeFilter(null)} className={"text-sm leading-[17px] mb-4 flex items-center" + (currentFilter === null ? " font-bold" : "")}>
                                            {
                                                currentFilter === null &&
                                                <span className="block h-[6px] w-[6px] rounded-full bg-red mr-1"></span>
                                            }
                                            <span>{t('screens.services.allServices')}</span>
                                        </div>
                                        {
                                            groupServiceCategories.map((serviceCategory, j) => {
                                                return (
                                                    <div onClick={changeFilter(serviceCategory.type)} key={j} className={"text-sm leading-[17px] flex items-center" + (j === groupServiceCategories.length - 1 ? '' : ' mb-4') + (currentFilter === serviceCategory.type ? ' font-bold' : "")}>
                                                        {
                                                            currentFilter === serviceCategory.type &&
                                                            <span className="block h-[6px] w-[6px] rounded-full bg-red mr-1"></span>
                                                        }
                                                        <span>{serviceCategory.type}</span>
                                                    </div>
                                                )
                                            })
                                        }
                                    </div>
                                }
                            </div>
                        }
                    </div>

                    {
                        showedTab === 'mainServices' &&
                        <>
                            {
                                services.mainServices.length > 0 &&
                                <div>
                                    {
                                        visualizationType === 'grid' &&
                                        <div className="grid grid-cols-12 gap-6">
                                            {
                                                filterServices.map((service, index) => {
                                                    return (
                                                        <div
                                                            key={index}
                                                            className="col-span-12 md:col-span-6 lg:col-span-4 xl:col-span-3 md:flex md:flex-col md:justify-between rounded-[8px] bg-lightGrey shadow-card p-4"
                                                        >
                                                            <div>
                                                                <div className="mb-1 flex items-center justify-between">
                                                                    <div className="text-red uppercase text-sm font-semibold">{service.type}</div>
                                                                    {
                                                                        !!service.description &&
                                                                        <div className="relative mt-[1px] cursor-pointer">
                                                                            <div id={"mainService-tooltip-trigger" + index}>
                                                                                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                                    <path fillRule="evenodd" clipRule="evenodd" d="M18 9C18 13.879 13.879 18 9 18C4.121 18 0 13.879 0 9C0 4.121 4.121 0 9 0C13.879 0 18 4.121 18 9ZM16 9C16 5.206 12.794 2 9 2C5.206 2 2 5.206 2 9C2 12.794 5.206 16 9 16C12.794 16 16 12.794 16 9ZM10 10V5H8V10H10ZM10 13V11H8V13H10Z" fill="#CF0A2C" />
                                                                                </svg>
                                                                            </div>

                                                                            <Tooltip triggerId={"mainService-tooltip-trigger" + index} id={"mainService-" + index} title={service.name} description={service.description} />
                                                                        </div>
                                                                    }
                                                                </div>
                                                                <div className="text-black font-bold text-[18px] leading-[27px] mb-4">{!!service.name ? service.name : service.type}</div>
                                                            </div>
                                                            <div className="flex items-center text-sm leading-[17px]">
                                                                <div className="mr-[10px]">{t('screens.dashboard.widgets.services.status')}:</div>
                                                                <div className="flex items-center">
                                                                    <div>
                                                                        {
                                                                            getMainServicesStatusIcon(service.statusCode)
                                                                        }
                                                                    </div>
                                                                    <div className="font-bold ml-1">{service.status}</div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })
                                            }
                                        </div>
                                    }

                                    {
                                        visualizationType === 'table' &&
                                        <div className="ag-theme-alpine" style={{ width: '100%', height: '100%' }}>
                                            <AgGridReact
                                                ref={gridRef}
                                                rowData={filterServices} // Row Data for Rows 
                                                columnDefs={columnDefs} // Column Defs for Columns 
                                                defaultColDef={{
                                                    flex: 1,
                                                    minWidth: 100,
                                                }}
                                                onGridReady={onGridReady}
                                                suppressRowClickSelection
                                                suppressCellFocus
                                                suppressMovableColumns
                                            />
                                        </div>
                                    }
                                </div>
                            }

                            {
                                services.mainServices.length === 0 &&
                                <div>
                                    {t('screens.services.emptyServices')}
                                </div>
                            }
                        </>
                    }

                    {
                        showedTab === 'additionalServices' &&
                        <>
                            {
                                services.additionalServices.length > 0 &&
                                <div>
                                    {
                                        visualizationType === 'grid' &&
                                        <div className="grid grid-cols-12 gap-6">
                                            {
                                                filterServices.map((service, index) => {
                                                    return (
                                                        <div
                                                            key={index}
                                                            className="col-span-12 md:col-span-6 lg:col-span-4 xl:col-span-3 md:flex md:flex-col md:justify-between rounded-[8px] bg-lightGrey shadow-card p-4"
                                                        >
                                                            <div>
                                                                <div className="mb-1 flex items-center justify-between">
                                                                    <div className="text-red uppercase text-sm font-semibold">{service.type}</div>
                                                                    {
                                                                        !!service.description &&
                                                                        <div className="relative mt-[1px] cursor-pointer">
                                                                            <div id={"mainService-tooltip-trigger" + index}>
                                                                                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                                    <path fillRule="evenodd" clipRule="evenodd" d="M18 9C18 13.879 13.879 18 9 18C4.121 18 0 13.879 0 9C0 4.121 4.121 0 9 0C13.879 0 18 4.121 18 9ZM16 9C16 5.206 12.794 2 9 2C5.206 2 2 5.206 2 9C2 12.794 5.206 16 9 16C12.794 16 16 12.794 16 9ZM10 10V5H8V10H10ZM10 13V11H8V13H10Z" fill="#CF0A2C" />
                                                                                </svg>
                                                                            </div>

                                                                            <Tooltip triggerId={"mainService-tooltip-trigger" + index} id={"mainService-" + index} title={service.name} description={service.description} />
                                                                        </div>
                                                                    }
                                                                </div>
                                                                <div className="text-black font-bold text-[18px] leading-[29px] mb-4">{!!service.name ? service.name : service.type}</div>
                                                            </div>
                                                            <div className="flex items-center text-sm leading-[17px]">
                                                                <div className="mr-[10px]">{t('screens.dashboard.widgets.services.status')}:</div>
                                                                <div className="flex items-center">
                                                                    <div>
                                                                        {
                                                                            getAdditionalServicesStatusIcon(service.statusCode)
                                                                        }
                                                                    </div>
                                                                    <div className="font-bold ml-1">{service.status}</div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })
                                            }
                                        </div>
                                    }

                                    {
                                        visualizationType === 'table' &&
                                        <div className="ag-theme-alpine" style={{ width: '100%', height: '100%' }}>
                                            <AgGridReact
                                                ref={gridRef}
                                                rowData={filterServices} // Row Data for Rows 
                                                columnDefs={columnDefs} // Column Defs for Columns 
                                                defaultColDef={{
                                                    flex: 1,
                                                    minWidth: 100,
                                                }}
                                                onGridReady={onGridReady}
                                                suppressRowClickSelection
                                                suppressCellFocus
                                                suppressMovableColumns
                                            />
                                        </div>
                                    }
                                </div>
                            }

                            {
                                services.additionalServices.length === 0 &&
                                <div>
                                    {t('screens.services.emptyServices')}
                                </div>
                            }
                        </>
                    }
                </>
            )}
        </div>
    )
}

export default Services;